import { ValidationError } from "../../../../errors";
import { fetchData } from "../../../../clients/api";
import type { OstCondition, EdiphiProduct } from "./types";
import { UOM_MAP } from "./types";

/**
 * Resolve `item_code` for the given Ediphi product ids in parallel chunks. Returns a
 * map keyed by the canonicalized product id. Missing / failed lookups are simply
 * absent from the map — callers must fall back gracefully.
 */
async function fetchProductItemCodes(productIds: string[]): Promise<Map<string, string>> {
  const unique = Array.from(new Set(productIds.map((id) => id.trim().toLowerCase()).filter(Boolean)));
  const byId = new Map<string, string>();
  if (unique.length === 0) return byId;

  const PRODUCT_LOOKUP_CONCURRENCY = 8;
  for (let i = 0; i < unique.length; i += PRODUCT_LOOKUP_CONCURRENCY) {
    const chunk = unique.slice(i, i + PRODUCT_LOOKUP_CONCURRENCY);
    const responses = await Promise.all(chunk.map((id) => fetchData({ table: "products", id })));
    for (const response of responses) {
      if (response == null || response.data == null) continue;
      const raw = response.data as { id?: string; item_code?: string | null };
      const id = raw?.id;
      const itemCode = raw?.item_code;
      if (typeof id === "string" && id && typeof itemCode === "string" && itemCode.trim() !== "") {
        byId.set(id.trim().toLowerCase(), itemCode);
      }
    }
  }
  return byId;
}

/**
 * Direct-children traversal mirroring the structural walk
 * `XML_ROOT > Bid > BidConditions > BidCondition > BidAreaConditions > BidAreaCondition`
 * exactly. Using descendant queries (`getElementsByTagName`) would be shorter but
 * would over-count if the OST schema ever nested the same tag name at a deeper
 * level — direct-child iteration is the same shape as the server-side parser.
 */
function directChildren(parent: Element, tagName: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < parent.children.length; i++) {
    const child = parent.children[i];
    if (child.tagName === tagName) result.push(child);
  }
  return result;
}

function parseFloatAttr(el: Element, name: string): number {
  const raw = el.getAttribute(name) ?? "0";
  const value = parseFloat(raw || "0");
  return Number.isFinite(value) ? value : 0;
}

/**
 * Locate the embedded `{"ediphi_products": …}` JSON object inside a free-form
 * `Notes` string. Returns the matched span (so callers can both parse it and
 * strip it from the persisted notes) or `null` if no marker is present.
 *
 * We can't use a single regex for this because the payload contains nested
 * braces (e.g. `{"ediphi_products":[{"qt1":"abc"}]}`): a greedy `/\{.*}/`
 * over-matches across unrelated user-entered braces in the surrounding notes,
 * and a lazy `/\{.*?}/` stops at the first inner `}`. Instead we anchor on the
 * marker key and brace-balance forward, with rudimentary string-literal
 * awareness so braces inside JSON strings don't throw off the depth counter.
 */
function extractEdiphiNotesJson(notes: string): { raw: string; start: number; end: number } | null {
  const marker = /\{\s*"ediphi_products"\s*:/.exec(notes);
  if (!marker) return null;

  const start = marker.index;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < notes.length; i++) {
    const ch = notes[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\") {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === "{") {
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0) {
        const end = i + 1;
        return { raw: notes.slice(start, end), start, end };
      }
    }
  }
  return null;
}

export interface ProcessedOstFile {
  conditions: OstCondition[];
  jobName: string | null;
}

export async function processOSTFile(xml: string): Promise<ProcessedOstFile> {
  const trimmedXml = xml.replace(/^\uFEFF/, "").trim();

  if (!trimmedXml.startsWith("<")) {
    throw new ValidationError(
      "Invalid file format. The uploaded file does not appear to be a valid OST/XML file. Please ensure you are uploading an OST export file."
    );
  }

  const doc = new DOMParser().parseFromString(trimmedXml, "application/xml");

  // DOMParser signals malformed XML by returning a document containing a
  // <parsererror> element rather than throwing. Surface it the same way the
  // previous xml2js parser surfaced its thrown error.
  const parseError = doc.getElementsByTagName("parsererror")[0];
  if (parseError) {
    const parserError = (parseError.textContent || "Unknown XML parse error").trim();
    throw new ValidationError("Failed to parse OST XML", { parserError });
  }

  const root = doc.documentElement;
  if (!root || root.nodeName !== "XML_ROOT") {
    throw new ValidationError("Invalid OST structure: Bid object not found");
  }

  const bids = directChildren(root, "Bid");
  if (bids.length === 0) {
    throw new ValidationError("Invalid OST structure: Bid object not found");
  }

  const conditions: OstCondition[] = [];
  let order = 1;

  for (const bid of bids) {
    for (const bidConditionsWrapper of directChildren(bid, "BidConditions")) {
      for (const bidCondition of directChildren(bidConditionsWrapper, "BidCondition")) {
        const Name = bidCondition.getAttribute("Name") ?? "";
        const Notes = bidCondition.getAttribute("Notes") ?? "";
        const UOM1 = bidCondition.getAttribute("UOM1") ?? "";
        const UOM2 = bidCondition.getAttribute("UOM2") ?? "";
        const UOM3 = bidCondition.getAttribute("UOM3") ?? "";
        // UID is the file-internal int id and the FK target for BidAreaCondition / BidTakeoff,
        // so it must be present and unique within the file. Without it we cannot derive a
        // stable qtoId, so skip the row rather than emitting one with a synthetic fallback.
        const UID = bidCondition.getAttribute("UID")?.trim() ?? "";
        if (!UID) {
          console.warn(`BidCondition has no UID — skipping (Name="${Name}")`);
          continue;
        }

        // The embedded `ediphi_products` JSON is optional product-link metadata.
        // If the marker is missing or the payload is malformed, log a warning
        // and continue with `jsonData = null` rather than aborting the whole
        // file — the row still imports, it just won't auto-link to a product.
        const productMatch = extractEdiphiNotesJson(Notes);
        let jsonData: { ediphi_products?: EdiphiProduct[] } | null = null;
        if (productMatch) {
          try {
            jsonData = JSON.parse(productMatch.raw);
          } catch (jsonError) {
            console.warn(
              `BidCondition "${Name}" (UID=${UID}): inline ediphi_products JSON in Notes is malformed, ignoring`,
              jsonError
            );
          }
        }

        const accumulated = { qt1: 0, qt2: 0, qt3: 0 };
        const areaWrappers = directChildren(bidCondition, "BidAreaConditions");
        if (areaWrappers.length === 0) {
          console.warn("BidAreaConditions is not present — skipping");
          continue;
        }
        for (const areaWrapper of areaWrappers) {
          for (const bac of directChildren(areaWrapper, "BidAreaCondition")) {
            accumulated.qt1 += parseFloatAttr(bac, "Quantity1");
            accumulated.qt2 += parseFloatAttr(bac, "Quantity2");
            accumulated.qt3 += parseFloatAttr(bac, "Quantity3");
          }
        }

        const uomKeys: Record<number, string> = { 1: UOM1, 2: UOM2, 3: UOM3 };
        const qtKeys: Record<number, number> = {
          1: accumulated.qt1,
          2: accumulated.qt2,
          3: accumulated.qt3,
        };

        // Strip the exact JSON span we extracted (not a fresh greedy regex),
        // so unrelated braces in user-entered notes survive intact.
        const cleanedNotes = (productMatch ? Notes.slice(0, productMatch.start) + Notes.slice(productMatch.end) : Notes)
          .trim()
          .substring(0, 500);

        for (const idx of [1, 2, 3] as const) {
          const quantity = qtKeys[idx];
          if (quantity > 0) {
            const uom = UOM_MAP[uomKeys[idx]] ?? null;
            const key = `qt${idx}` as keyof EdiphiProduct;
            const product = jsonData?.ediphi_products?.filter((p) => Boolean(p[key]))?.[0]?.[key];

            conditions.push({
              name: Name,
              quantity,
              uom,
              notes: cleanedNotes,
              product,
              order: order++,
              uid: UID,
              slotIdx: idx,
            });
          }
        }
      }
    }
  }

  if (!conditions.length) {
    throw new ValidationError("File is empty");
  }

  // Replace the auto-incremented order with the product's item_code on rows that
  // resolved to an Ediphi product. Rows without a product (or whose product has no
  // item_code) keep their original numeric order so the QTO Review sheet still has
  // a stable identifier in the "Order" column.
  const productIds = conditions
    .map((c) => c.product)
    .filter((p): p is string => typeof p === "string" && p.trim() !== "");
  const itemCodeById = await fetchProductItemCodes(productIds);
  for (const c of conditions) {
    if (c.product) {
      const itemCode = itemCodeById.get(c.product.trim().toLowerCase());
      if (itemCode) c.order = itemCode;
    }
  }

  const jobName =
    bids
      .map((b) => b.getAttribute("JobName"))
      .find((name): name is string => typeof name === "string" && name.length > 0) ?? null;

  return { conditions, jobName };
}
